import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { marketplaceApi } from '../../api/marketplace';
import CardImageLightbox from './CardImageLightbox';
import { getTeamAbbr } from '../../utils/teamAbbr';
import { useLanguage } from '../../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../../context/ConfirmD10SContext';

const SaleCard = ({ sale, user, onPurchaseSuccess, onError }) => {
  const { t } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isCopiesOpen, setIsCopiesOpen] = useState(false);
  const [copies, setCopies] = useState([]);
  const [copiesLoading, setCopiesLoading] = useState(false);
  const [copiesError, setCopiesError] = useState('');
  const [purchasingCopyIds, setPurchasingCopyIds] = useState({});
  const salePrice = parseFloat(sale.price) || 0;
  const availableCount = parseInt(sale.available_count, 10) || 1;
  const hasMultipleCopies = availableCount > 1;
  const xpLevel = sale?.card?.xp_level ?? sale?.xp_level ?? sale?.card?.xpLevel ?? sale?.xpLevel;
  const xpPercentage = sale?.card?.xp_percentage ?? sale?.xp_percentage ?? sale?.card?.xpPercentage ?? sale?.xpPercentage;
  const hasXpLevel = xpLevel !== null && xpLevel !== undefined && !Number.isNaN(Number(xpLevel));
  const hasXpPercentage = xpPercentage !== null && xpPercentage !== undefined && !Number.isNaN(Number(xpPercentage));
  const displayCardId = sale.sample_card_id ?? sale.card_id ?? sale.card?.id;

  useEffect(() => {
    setIsCopiesOpen(false);
    setCopies([]);
    setCopiesError('');
    setPurchasingCopyIds({});
  }, [sale.id]);

  const loadCopies = async () => {
    if (!sale.template_id) return;
    setCopiesLoading(true);
    setCopiesError('');
    try {
      const response = await marketplaceApi.getSalesByTemplate(sale.template_id);
      if (response?.success) {
        setCopies(response.sales || []);
      } else {
        setCopies([]);
        setCopiesError(response?.error || t('marketplaceCards.copiesLoadFailed'));
      }
    } catch (error) {
      setCopies([]);
      setCopiesError(error.message || t('marketplaceCards.copiesLoadFailed'));
    } finally {
      setCopiesLoading(false);
    }
  };

  const handleOpenCopies = () => {
    setIsCopiesOpen(true);
    if (copies.length === 0 && !copiesLoading) {
      loadCopies();
    }
  };

  const purchaseSale = async (saleToPurchase, setBusy) => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToPurchase'));
      return;
    }

    if (saleToPurchase.seller_id === (user?.linkedUserId ?? user.id)) {
      onError(t('marketplaceCards.purchaseOwnCard'));
      return;
    }

    const price = parseFloat(saleToPurchase.price) || 0;
    const ok = await confirm(price, t('marketplaceCards.buyNowAction'));
    if (!ok) return;

    setBusy(true);
    try {
      await marketplaceApi.purchase(saleToPurchase.id, {
        buyerId: user?.linkedUserId ?? user.id
      });
      onPurchaseSuccess();
      setIsCopiesOpen(false);
    } catch (error) {
      onError(error.message || t('marketplaceCards.purchaseFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handlePurchase = async () => {
    if (hasMultipleCopies) {
      handleOpenCopies();
      return;
    }

    await purchaseSale(sale, setIsPurchasing);
  };

  const handlePurchaseCopy = async (copy) => {
    setPurchasingCopyIds((prev) => ({ ...prev, [copy.id]: true }));
    await purchaseSale(copy, (busy) => {
      setPurchasingCopyIds((prev) => ({ ...prev, [copy.id]: busy }));
    });
  };

  return (
    <div className="football-card glass-football p-6 card-hover relative">
      {/* Badge with available count */}
      {hasMultipleCopies && (
        <button
          type="button"
          onClick={handleOpenCopies}
          className="absolute top-2 right-2 z-10 bg-green-600 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg border-2 border-white/50 hover:bg-green-700 transition-colors"
          aria-label={t('marketplaceCards.viewCopiesAria', { count: availableCount })}
        >
          x{availableCount}
        </button>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="marketplace-badge marketplace-badge-sale px-3 py-1 text-sm font-semibold">
          💰 {t('marketplaceCards.buyNowBadge')}
        </span>
      </div>
      
      <div className="flex items-center gap-3 mb-3">
        <CardImageLightbox
          card={sale}
          alt={sale.player?.display_name || sale.player?.full_name || t('marketplaceCards.playerAlt')}
          thumbnailClassName="w-12 h-12 rounded-full object-cover border border-white/60 shadow-sm"
        />
        <h3 className="text-xl font-bold text-[#1e293b]">
        {sale.player?.display_name || sale.player?.full_name || `Card #${sale.card_id}`}
        </h3>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">{t('marketplaceCards.positionLabel')}:</span>
            <span className="text-[#1e293b] font-semibold">{sale.player?.position || t('common.notAvailable')}</span>
        </div>
        {sale.player?.team && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">{t('marketplaceCards.teamLabel', 'Team')}:</span>
            <span className="text-[#1e293b] font-semibold">{getTeamAbbr(sale.player.team)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">{t('marketplaceCards.rarityLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">
              ⭐ {sale.rarity?.display_name || sale.rarity?.name || t('common.notAvailable')}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.xpLevelLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">
            {hasXpLevel ? `Lv ${Number(xpLevel)}${hasXpPercentage ? ` (${Number(xpPercentage).toFixed(0)}%)` : ''}` : t('common.notAvailable')}
          </span>
        </div>
        {['legend', 'divinity'].includes(String(sale.cardKind || sale.card_kind || '').toLowerCase()) &&
         (sale.scoreBonusPercent != null || sale.scoreBonusPosition || sale.scoreBonusLeague) && (
          <div className="text-sm p-2 rounded-lg bg-[#22C55E]/10 border border-[#22C55E]/30">
            <div className="text-[#64748b] font-medium mb-1">{t('marketplaceCards.bonusLabel')}:</div>
            <div className="space-y-0.5 text-[#1e293b]">
              {sale.scoreBonusPosition && (
                <div>{t('marketplaceCards.bonusPosition')}: <span className="text-[#22C55E] font-semibold">{sale.scoreBonusPosition}</span></div>
              )}
              {sale.scoreBonusLeague && (
                <div>{t('marketplaceCards.bonusLeague')}: <span className="text-[#22C55E] font-semibold">{sale.scoreBonusLeague}</span></div>
              )}
              {sale.scoreBonusPercent != null && (
                <div>{t('marketplaceCards.bonusScore')}: <span className="text-[#22C55E] font-semibold">+{Number(sale.scoreBonusPercent)}%</span></div>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">Card ID:</span>
            <span className="text-[#94a3b8] font-mono text-xs">#{displayCardId ?? t('common.notAvailable')}</span>
        </div>
        <div className="border-t border-[#E2E8F0] pt-2 mt-2">
          <div className="flex items-center justify-between">
              <span className="text-[#64748b] text-sm font-medium">{t('marketplaceCards.priceLabel')}:</span>
            <span className="text-[#4169E1] font-bold text-xl">{salePrice.toFixed(2)} DIOS</span>
          </div>
        </div>
      </div>
      
      <button 
        className="btn-football w-full"
        onClick={handlePurchase}
        disabled={isPurchasing || !user?.id || (!hasMultipleCopies && sale.seller_id === (user?.linkedUserId ?? user.id))}
      >
        {isPurchasing
          ? t('marketplaceCards.processing')
          : hasMultipleCopies
            ? t('marketplaceCards.viewCopiesPurchase')
            : `🛒 ${t('marketplaceCards.buyNowAction')}`}
      </button>

      {isCopiesOpen && ReactDOM.createPortal(
        <div
          className="marketplace-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ width: '100vw', height: '100vh' }}
          onClick={() => setIsCopiesOpen(false)}
        >
          <div
            className="football-card glass-football marketplace-modal-card w-full max-w-4xl p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold uppercase tracking-wider">
                {t('marketplaceCards.availableCopies', { count: availableCount })}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsCopiesOpen(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            {copiesLoading && (
              <div className="text-center py-8 text-white/70">{t('marketplaceCards.loadingCopies')}</div>
            )}

            {!copiesLoading && copiesError && (
              <div className="text-center py-6 text-red-300">{copiesError}</div>
            )}

            {!copiesLoading && !copiesError && copies.length === 0 && (
              <div className="text-center py-6 text-white/70">{t('marketplaceCards.noCopies')}</div>
            )}

            {!copiesLoading && copies.length > 0 && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {copies.map((copy) => {
                  const copyPrice = parseFloat(copy.price) || 0;
                  const copyCardId = copy.sample_card_id ?? copy.card_id ?? copy.card?.id;
                  const isPurchasingCopy = purchasingCopyIds[copy.id];
                  const isOwnCopy = copy.seller_id === (user?.linkedUserId ?? user?.id);
                  return (
                    <div
                      key={copy.id}
                      className="border border-white/10 p-4 bg-black/40"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-white">
                          {t('marketplaceCards.saleNumber', { id: copy.id })}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <div className="text-white/60 text-xs">Card ID</div>
                          <div className="text-white font-mono text-xs">#{copyCardId ?? t('common.notAvailable')}</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">{t('marketplaceCards.priceLabel')}</div>
                          <div className="text-[#00D9FF] font-bold">{copyPrice.toFixed(2)} DIOS</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">{t('marketplaceCards.ownerLabel')}</div>
                          <div className="text-white font-semibold">
                            {copy.seller?.username || copy.seller_id || t('common.unknown')}
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn-football w-full"
                            onClick={() => handlePurchaseCopy(copy)}
                            disabled={isPurchasingCopy || !user?.id || isOwnCopy}
                          >
                            {isPurchasingCopy ? t('marketplaceCards.processing') : `🛒 ${t('marketplaceCards.buyNowAction')}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SaleCard;
